import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { FormErrorSummary } from '@/components/catalog/FormShell';
import { isRealDate, without } from '@/components/catalog/form-utils';
import { DocumentLinePicker } from '@/components/client-deliverables/DocumentLinePicker';
import { ApiError, api } from '@/lib/api';
import {
  describeCandidate,
  loadLinkCandidatesFor,
  withRecommendation,
} from '@/lib/deliverable-link';
import { useLineProject } from '@/lib/use-line-project';
import { useReferenceOptions } from '@/lib/use-reference-options';
import {
  Button,
  Combobox,
  Field,
  Input,
  Modal,
  MultiCombobox,
  Radio,
  Textarea,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type {
  ClientDeliverable,
  ClientDeliverableCreateInput,
  EngagementRole,
  LaeSpecialist,
  LinkCandidate,
  LinkConflictDetail,
} from '@/lib/types';

/**
 * THE ONE THING ON THE TRACKER SOMEBODY CREATES — a row with no document.
 *
 * ⚠️ **THIS IS NOT "ADD A ROW TO THE TRACKER", AND THAT DISTINCTION IS THE WHOLE
 * RISK OF THE CONTROL.** `/client-deliverables` is a query over quote and
 * invoice LINES: the ordinary way a row appears is that somebody sells the work,
 * and creating one here for work that IS on a quote puts the same work on the
 * tracker twice, under two rows that never reconcile — a 201, nothing failing,
 * and two figures where the business has one. The page's own header comment says
 * why there is no create form for the derived populations; that reasoning is
 * untouched by this file.
 *
 * What this creates is the THIRD population: a STANDALONE row — complimentary
 * work, and work billed from another budget. The workbook this tracker replaces
 * carries 13 of them ("complimentary", "paid from 1-1 Budget", "no invoice to be
 * sent"). They exist so nobody has to raise a zero-value quote to make the
 * tracker behave, and they are the only rows whose company, product, promised
 * quantity, project and regions live on the record rather than on a line.
 *
 * The wording of the trigger and of this modal's title is the only thing
 * standing between a reader and that mistake, which is why both name the
 * absence of a document rather than saying "deliverable" flatly.
 *
 * ## The contract
 *
 * `POST /client-deliverables` → `createStandaloneSchema`, which is `.strict()`.
 * Required: `companyId`, `productId`, `promised`. Optional: `projectId`,
 * `billingLabel`, `regionIds`, and the eight keys of
 * `ClientDeliverableTypedFields`. **An unknown key, or an empty string where an
 * id is expected, is a 422 naming it** — so the payload is built from an
 * explicit key list below and never from a spread of form state.
 *
 * ⚠️ **`companyId` and `productId` are settable HERE AND NOWHERE ELSE, EVER.**
 * `updateSchema` does not declare them: a row cannot change population, and
 * changing what a standalone row is about while keeping its id and its recorded
 * progress is exactly what that refusal prevents. Everything else on this form
 * is patchable afterwards — the eight typed keys through `InlineTrackerFields`
 * on the row itself, the rest through the API.
 *
 * ⚠️ **Nothing here writes `travel` or `accountOwnerId` — and as of 2026-09-09
 * they are guarded DIFFERENTLY, which is the part to read before adding either.**
 * *SUPERSEDED: "Both are absent from `ClientDeliverableCreateInput` — `travel`
 * because it is derived from the product's travel product (migration 115) and
 * `accountOwnerId` because the owner retired the column from this app on
 * 2026-08-31. The guard is the type, so no payload this file can build expresses
 * either."*
 *
 * `accountOwnerId` is still guarded by the type. **`travel` is not**: it went
 * back onto `ClientDeliverableTypedFields` as an override, and this form's input
 * type extends that, so `tsc` would now let a payload carry it. What keeps it
 * out here is this file's own shape — `travel` is not a key of `FormValues`, so
 * `KNOWN_WRITE_KEYS` does not name it and `buildPayload`'s explicit key list has
 * nothing to send.
 *
 * ## Why Travel is deliberately NOT a control on this form
 *
 * It is a real candidate and was considered rather than overlooked. A standalone
 * row has no document, so the derivation behind Travel can only ever answer "no"
 * for one — which makes this the ONE population where an override is the only
 * way to record travel at all. Two things decided it the other way:
 *
 *  - ⚠️ **Whether `createStandaloneSchema` accepts the key has not been
 *    established here.** It is `.strict()`, so if it does not, this control's
 *    only outcome is a 422 with `path: ''` naming a field the reader can see —
 *    on a create that would otherwise have succeeded. A control whose outcome
 *    may be nothing but a refusal is the affordance this codebase refuses to
 *    render (Xero's `sync` button is the standing example).
 *  - **It is one click away on the row it creates.** Unlike `companyId` and
 *    `productId`, which are settable here and NOWHERE else ever, `travel` is
 *    patchable in place the moment the row appears — and setting it there shows
 *    the reader the derived answer they are overruling, which this form cannot.
 *
 * **Adding it is one `Field` plus one key on `FormValues`, once the backend
 * confirms the create schema takes it.** Ask; do not infer it from the key being
 * accepted on `PATCH` and `PUT`.
 *
 * ## ⚠️ The project picker is scoped to the PRODUCT, and it is PRODUCT-FIRST
 *
 * **A project names exactly one product** (`d_projects.product_id` is `NOT NULL`
 * and there is no join table), so an unscoped picker offers projects belonging
 * to some other product — and `createStandaloneSchema` **does not check that the
 * project's product matches the row's**. Unlike a quote or invoice LINE, where
 * that mismatch is refusal #5 (`project-wrong-product`) and the server is the
 * backstop, here **nothing refuses it: it saves, 201, and the tracker row is
 * filed under a run that sells something else.** The client is the only guard,
 * which is why the scoping is not a convenience.
 *
 * Two consequences the rest of this file has to keep:
 *
 *  - **Changing the product CLEARS the project, in the same state update** — see
 *    `setProduct`. A project carried over from the previous product is exactly
 *    the row above, arrived at by not touching a control.
 *  - **With no product the picker is DISABLED with the reason stated**, not
 *    silently empty and not project-first. The line grid resolves from either
 *    end because picking a project there sets the line's product; that is not
 *    offered here because `productId` is a REQUIRED field of this record chosen
 *    from a server-searched list of 28, so "which product" is a question the
 *    reader has already been asked directly, and a second answer to it would be
 *    a second writer of a required value with no server-side reconciliation.
 *
 * `useLineProject` is the fetch, reused rather than reimplemented: it is the one
 * `GET /projects?productId=…&isActive=true` in the codebase, and its predicate
 * matches the server's own. Passing `null` for region, cycle and intake puts it
 * on its cheapest path — **exactly one request per product, no probes**, because
 * its follow-up questions only arise when one of those three narrowed the set.
 *
 * ## "Will be billed on" (owner, 2026-10-16)
 *
 * Once client and product are chosen, the form offers the deal / quote /
 * invoice lines selling that product to that client (`DocumentLinePicker`), so
 * work that WILL be billed is one row on the tracker rather than two. The
 * server's `/:id/link-candidates` needs a row id and there is none yet, so the
 * candidates are built client-side by `lib/deliverable-link.ts`. Default is
 * none chosen; nothing is auto-picked.
 *
 * ⚠️ **Two writes, no transaction.** Submit creates the standalone row exactly
 * as before, THEN `POST /client-deliverables/:id/link`. When a line is chosen
 * the modal does not close on create: it switches to a result view that
 * reports each step on its own ("Added to the tracker" + "Linked to …" or the
 * link's refusal) and offers the retry — on the CREATED row, never a second
 * create. A `409 LINK_CONFLICT` lists the disagreeing fields and the retry
 * sends the reader's per-field `resolutions`. The link choice is keyed on the
 * client + product it was offered for, so changing either drops it.
 */

/* -------------------------------------------------------------------------
 * The server's own limits
 * --------------------------------------------------------------------- */

/** `count()` is `INT UNSIGNED`: 0 .. 4,294,967,295. Both counts share it. */
const COUNT_MAX = 4_294_967_295;
/** `optionalText(60)` — "complimentary", "paid from 1-1 Budget". */
const BILLING_LABEL_MAX = 60;
/** `optionalText(4_000)` — a whole booking specification. */
const BOOKING_SPEC_MAX = 4_000;
/** `optionalText(2_000)` on both. */
const BILLING_NOTE_MAX = 2_000;
const ADS_PLAN_MAX = 2_000;
/** `z.array(...).max(50)` on `regionIds`. */
const REGIONS_MAX = 50;

/** One page of picker options; the server caps `perPage` at 100 on all of these. */
const OPTION_PER_PAGE = 100;

const ENGAGEMENT_ROLE_OPTIONS: readonly ComboboxOption[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
];

/* -------------------------------------------------------------------------
 * Form state
 * --------------------------------------------------------------------- */

/**
 * Every value a control holds, keyed on the WIRE key it is sent under.
 *
 * ⚠️ Keeping the two spellings identical is what makes `KNOWN_WRITE_KEYS`
 * derivable from this object rather than written out beside it — a hand-kept
 * list drifts away from the controls, and the failure is silent: a 422 naming a
 * key nobody listed vanishes from the banner, lands on no input, and still
 * inflates the "fields need attention" count.
 */
interface FormValues {
  companyId: string;
  productId: string;
  promised: string;
  projectId: string;
  billingLabel: string;
  regionIds: string[];
  completed: string;
  deliveryLeadId: string;
  specialistId: string;
  startDate: string;
  endDate: string;
  engagementRole: string;
  bookingSpec: string;
  billingNote: string;
  adsPlan: string;
}

function emptyValues(): FormValues {
  return {
    companyId: '',
    productId: '',
    promised: '',
    projectId: '',
    billingLabel: '',
    regionIds: [],
    completed: '',
    deliveryLeadId: '',
    specialistId: '',
    startDate: '',
    endDate: '',
    engagementRole: '',
    bookingSpec: '',
    billingNote: '',
    adsPlan: '',
  };
}

/**
 * The `path` values a 422 may name that this form can actually MARK.
 *
 * Derived from the value object, so it cannot name a key with no control or
 * miss one that has. Anything outside it — the `.strict()` empty-path refusal, a
 * key this form does not render — is unroutable and belongs in the banner.
 */
const KNOWN_WRITE_KEYS: readonly string[] = Object.keys(emptyValues());

/** Labels, in one place, so an error and its control cannot disagree. */
const LABELS = {
  companyId: 'Client',
  productId: 'Product',
  promised: 'Promised',
  projectId: 'Project',
  billingLabel: 'Why there is no document',
  regionIds: 'Regions',
  completed: 'Completed',
  deliveryLeadId: 'Delivery lead',
  specialistId: 'Specialist',
  startDate: 'Start date',
  endDate: 'End date',
  engagementRole: 'Engagement',
  bookingSpec: 'Booking specifications',
  billingNote: 'Billing note',
  adsPlan: 'Ads plan',
} as const;

/* -------------------------------------------------------------------------
 * The payload
 * --------------------------------------------------------------------- */

/**
 * Builds the create body from an EXPLICIT key list — never a spread.
 *
 * Two rules the schema makes unforgiving, both of which fail as a 422 naming a
 * field the reader can see is blank:
 *
 *  - **An empty string is not an absent key.** `optionalRefId()` accepts a
 *    number or a NON-EMPTY string, so `projectId: ''` is refused rather than
 *    read as "no project". Every optional key is omitted when its control is
 *    empty.
 *  - **`regionIds: []` CLEARS rather than leaves alone.** On a create there is
 *    nothing to clear, so the two are indistinguishable in effect today — the
 *    key is still omitted, because sending `[]` teaches this call site a habit
 *    that is wrong the moment the same builder is reused for a patch.
 *
 * ⚠️ **`promised` and `completed` are counts and `0` is a real figure**, so
 * neither is ever coalesced: a blank `completed` omits the key and lets the
 * column's own default stand, where `?? 0` would write "nothing delivered yet"
 * as though somebody had counted.
 *
 * Only reached once `validate()` has passed, so the three required ids are
 * non-empty and `Number()` here can never see `''`.
 */
function buildPayload(values: FormValues): ClientDeliverableCreateInput {
  const payload: ClientDeliverableCreateInput = {
    companyId: Number(values.companyId),
    productId: Number(values.productId),
    promised: Number(values.promised.trim()),
  };

  if (values.projectId !== '') payload.projectId = Number(values.projectId);
  if (values.billingLabel.trim() !== '') payload.billingLabel = values.billingLabel.trim();
  if (values.regionIds.length > 0) payload.regionIds = values.regionIds.map(Number);

  if (values.completed.trim() !== '') payload.completed = Number(values.completed.trim());
  if (values.deliveryLeadId !== '') payload.deliveryLeadId = Number(values.deliveryLeadId);
  // Omitted when empty, like every other id: the server resolves a named one
  // against the LAE list and stores its name, so there is nothing to send for none.
  if (values.specialistId !== '') payload.specialistId = Number(values.specialistId);
  if (values.startDate !== '') payload.startDate = values.startDate;
  if (values.endDate !== '') payload.endDate = values.endDate;
  if (values.engagementRole !== '') {
    payload.engagementRole = values.engagementRole as EngagementRole;
  }
  /*
   * Sent as typed, not trimmed: the server trims, and trimming here as well
   * would store a value that differs from the draft by a space. Only the
   * emptiness test trims, so a box holding spaces omits the key rather than
   * storing whitespace.
   */
  if (values.bookingSpec.trim() !== '') payload.bookingSpec = values.bookingSpec;
  if (values.billingNote.trim() !== '') payload.billingNote = values.billingNote;
  if (values.adsPlan.trim() !== '') payload.adsPlan = values.adsPlan;

  return payload;
}

function validateCount(raw: string, label: string): string | null {
  if (!/^\d+$/.test(raw)) return `${label} must be a whole number.`;
  const parsed = Number(raw);
  if (!Number.isSafeInteger(parsed) || parsed > COUNT_MAX) {
    return `${label} must be no greater than ${COUNT_MAX.toLocaleString()}.`;
  }
  return null;
}

function validate(values: FormValues): Record<string, string> {
  const found: Record<string, string> = {};

  if (values.companyId === '') found.companyId = 'Choose the client this is delivered to.';
  if (values.productId === '') found.productId = 'Choose the product being delivered.';

  const promised = values.promised.trim();
  if (promised === '') found.promised = 'Enter how many units were promised.';
  else {
    const message = validateCount(promised, 'Promised');
    if (message) found.promised = message;
  }

  // Blank is legitimate here and omits the key; 0 is a real figure and is not.
  const completed = values.completed.trim();
  if (completed !== '') {
    const message = validateCount(completed, 'Completed');
    if (message) found.completed = message;
  }

  if (values.billingLabel.trim().length > BILLING_LABEL_MAX) {
    found.billingLabel = `Must be ${BILLING_LABEL_MAX} characters or fewer.`;
  }

  /*
   * A regex alone accepts 2026-02-31, which passes validation and is then
   * refused by MySQL as a driver error — a 500 where a 422 belonged.
   */
  for (const key of ['startDate', 'endDate'] as const) {
    const value = values[key];
    if (value !== '' && !isRealDate(value)) found[key] = 'Enter a real date.';
  }
  /*
   * The ordering CHECK is the server's own, on the stored row, and a create
   * sends both halves — so this is the server's rule reported early, not a
   * convention invented here.
   */
  if (
    !found.startDate &&
    !found.endDate &&
    values.startDate !== '' &&
    values.endDate !== '' &&
    values.startDate > values.endDate
  ) {
    found.endDate = 'End date must be on or after the start date.';
  }

  return found;
}

/* -------------------------------------------------------------------------
 * The searchable pickers
 * --------------------------------------------------------------------- */

type SearchableSource = 'companies' | 'products';

interface SearchableOptions {
  options: ComboboxOption[];
  loading: boolean;
  failed: boolean;
  total: number;
  setTerm: (term: string) => void;
}

/**
 * Companies and products, searched server-side.
 *
 * ⚠️ **The page caps at `perPage` 100 and both lists are longer than that** —
 * 148 companies today. A picker missing its 101st option is indistinguishable
 * from that record not existing, so the truncation is reported as a `footnote`
 * on the list and the term is sent to the server rather than filtered locally.
 * `Combobox` debounces `onSearch` itself, so there is no second debounce here.
 *
 * ⚠️ **Products send `active: true`; companies have no such flag.** The
 * companion half of that house rule — injecting a stored value back so a
 * retired one is not silently reassigned — has nothing to inject on a CREATE:
 * there is no stored value yet, and a value chosen in this session is pinned by
 * `usePinnedChoice` below for a different reason (the option list moves under
 * the selection when the reader searches again).
 */
function useSearchableOptions(source: SearchableSource): SearchableOptions {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
  const loading = loadedTerm !== term;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const request: Promise<{ options: ComboboxOption[]; total: number }> =
      source === 'companies'
        ? api
            .listCompanies(
              {
                page: 1,
                perPage: OPTION_PER_PAGE,
                sort: 'name',
                order: 'asc',
                ...(term ? { search: term } : {}),
              },
              signal,
            )
            .then((result) => ({
              options: result.data.map((company) => ({
                value: String(company.id),
                label: company.name,
              })),
              total: result.meta.total,
            }))
        : api
            .listProducts(
              {
                page: 1,
                perPage: OPTION_PER_PAGE,
                // Opt-in, no default: a retired product is not something a new
                // record should start on, and the server would refuse it anyway.
                active: true,
                ...(term ? { search: term } : {}),
              },
              signal,
            )
            .then((result) => ({
              options: result.data.map((product) => ({
                value: String(product.id),
                label: product.productName,
                ...(product.productCode ? { hint: product.productCode } : {}),
              })),
              total: result.meta.total,
            }));

    request
      .then((result) => {
        setOptions(result.options);
        setTotal(result.total);
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setTotal(0);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [source, term]);

  return { options, loading, failed, total, setTerm };
}

/**
 * Keeps the chosen option in the list after the reader searches for something
 * else.
 *
 * `Combobox` renders its trigger from `options.find(o => o.value === value)`, so
 * a selection whose row has been searched away shows the PLACEHOLDER — the
 * control reads as empty while holding a real id, and the next thing anybody
 * does is pick again. Pinning the chosen `{value,label}` back is the create-time
 * analogue of `withStoredOption()`: same failure, different cause.
 */
function usePinnedChoice(options: ComboboxOption[], value: string): ComboboxOption[] {
  const [pinned, setPinned] = useState<ComboboxOption | null>(null);

  const match = options.find((option) => option.value === value);
  if (value === '' && pinned !== null) setPinned(null);
  else if (match && (pinned === null || pinned.value !== match.value)) setPinned(match);

  return useMemo(() => {
    if (pinned === null || options.some((option) => option.value === pinned.value)) return options;
    return [pinned, ...options];
  }, [options, pinned]);
}

/* -------------------------------------------------------------------------
 * "Will be billed on"
 * --------------------------------------------------------------------- */

type LinkValue = { kind: LinkCandidate['kind']; lineId: number };

/**
 * The candidates for one client + product. `key` is `''` when either is
 * missing, which fetches nothing. A result is held against the key it was
 * read for, so a stale answer can never render under a different pair.
 */
function useLinkCandidates(key: string, companyId: string, productId: string) {
  const [loaded, setLoaded] = useState<{
    key: string;
    candidates: LinkCandidate[] | null;
    error: string | null;
  } | null>(null);

  useEffect(() => {
    if (key === '') return;
    const controller = new AbortController();
    const { signal } = controller;
    loadLinkCandidatesFor(Number(companyId), Number(productId), signal)
      .then((candidates) => {
        if (signal.aborted) return;
        setLoaded({ key, candidates, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setLoaded({
          key,
          candidates: null,
          error:
            caught instanceof ApiError
              ? `Could not load this client's deal, quote and invoice lines. ${caught.message}`
              : "Could not load this client's deal, quote and invoice lines. Check your connection.",
        });
      });
    return () => controller.abort();
  }, [key, companyId, productId]);

  const current = loaded !== null && loaded.key === key ? loaded : null;
  return {
    candidates: current?.candidates ?? null,
    error: current?.error ?? null,
    loading: key !== '' && current === null,
  };
}

/** The `details` of a `409 LINK_CONFLICT`, read from the raw payload. */
function conflictDetailsOf(caught: ApiError): LinkConflictDetail[] {
  const raw = caught.detailPayload;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.path !== 'string') return [];
    return [
      {
        path: record.path,
        message: typeof record.message === 'string' ? record.message : record.path,
        manual: record.manual,
        document: record.document,
      },
    ];
  });
}

/** A conflict value as text. `null`/absent is "—", never blank and never 0. */
function conflictValueText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map(conflictValueText).join(', ');
  if (typeof value === 'object') {
    const named = (value as { name?: unknown }).name;
    return typeof named === 'string' ? named : JSON.stringify(value);
  }
  return String(value);
}

type LinkOutcome =
  | { state: 'linked'; label: string }
  | { state: 'failed'; message: string }
  | { state: 'conflict'; details: LinkConflictDetail[] };

/* -------------------------------------------------------------------------
 * The modal
 * --------------------------------------------------------------------- */

export interface DeliverableFormModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Staff for the delivery-lead picker, narrowed to the `delivery_lead` group.
   *
   * Passed down rather than fetched here: the page already calls
   * `useUserOptions` once for the whole table, and a second call from a modal
   * would be one more request against a bucket shared with the office for a list
   * that is already in memory.
   */
  staffOptions: ComboboxOption[];
  /**
   * Every LAE representative, read once by the page (`useLaeSpecialists`).
   * `null` is "not read, or the read failed" — never an empty list — and the
   * picker is disabled on it rather than offering nothing.
   */
  specialists: LaeSpecialist[] | null;
  /** The row the server created. The caller re-reads the list; it never inserts. */
  onCreated: (created: ClientDeliverable) => void;
}

export function DeliverableFormModal({
  open,
  onClose,
  staffOptions,
  specialists,
  onCreated,
}: DeliverableFormModalProps) {
  if (!open) return null;
  return (
    <DeliverableForm
      onClose={onClose}
      staffOptions={staffOptions}
      specialists={specialists}
      onCreated={onCreated}
    />
  );
}

function DeliverableForm({
  onClose,
  staffOptions,
  specialists,
  onCreated,
}: Omit<DeliverableFormModalProps, 'open'>) {
  const formId = useId();
  const companyRef = useRef<HTMLElement | null>(null);

  const [values, setValues] = useState<FormValues>(emptyValues);
  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const errors = useMemo(() => ({ ...clientErrors, ...serverErrors }), [clientErrors, serverErrors]);

  /*
   * "Will be billed on". The choice is held against the client + product it was
   * offered for, so changing either drops it without a second state update.
   */
  const linkKey =
    values.companyId !== '' && values.productId !== ''
      ? `${values.companyId}:${values.productId}`
      : '';
  const [linkChoice, setLinkChoice] = useState<{ key: string; value: LinkValue | null }>({
    key: '',
    value: null,
  });
  const link = linkChoice.key === linkKey && linkKey !== '' ? linkChoice.value : null;
  const linkCandidates = useLinkCandidates(linkKey, values.companyId, values.productId);
  const recommendedCandidates = useMemo(
    () =>
      linkCandidates.candidates === null
        ? null
        : withRecommendation(linkCandidates.candidates, values.promised),
    [linkCandidates.candidates, values.promised],
  );
  /** The row the create returned, once it has; from here on there is no second create. */
  const [created, setCreated] = useState<ClientDeliverable | null>(null);
  const [linkOutcome, setLinkOutcome] = useState<LinkOutcome | null>(null);
  const [resolutions, setResolutions] = useState<Record<string, 'manual' | 'document'>>({});
  const [linkFormError, setLinkFormError] = useState<string | null>(null);

  function chosenCandidate(choice: LinkValue | null): LinkCandidate | null {
    if (choice === null || linkCandidates.candidates === null) return null;
    return (
      linkCandidates.candidates.find(
        (candidate) => candidate.kind === choice.kind && candidate.lineId === choice.lineId,
      ) ?? null
    );
  }

  /** Step two. Never throws; the outcome is reported in the result view. */
  async function linkRow(
    row: ClientDeliverable,
    choice: LinkValue,
    answers: Record<string, 'manual' | 'document'> | null,
  ) {
    const candidate = chosenCandidate(choice);
    const label = candidate ? describeCandidate(candidate) : `the chosen ${choice.kind} line`;
    if (row.id === null) {
      setLinkOutcome({
        state: 'failed',
        message: 'The server did not return the new row’s id, so it could not be linked.',
      });
      return;
    }
    try {
      await api.linkClientDeliverable(row.id, {
        kind: choice.kind,
        lineId: choice.lineId,
        ...(answers !== null && Object.keys(answers).length > 0 ? { resolutions: answers } : {}),
      });
      setLinkOutcome({ state: 'linked', label });
      onCreated(row);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'LINK_CONFLICT') {
        const details = conflictDetailsOf(caught);
        if (details.length > 0) {
          setResolutions({});
          setLinkOutcome({ state: 'conflict', details });
          return;
        }
      }
      setLinkOutcome({
        state: 'failed',
        message:
          caught instanceof ApiError
            ? `${caught.message} Nothing was linked.`
            : 'Could not reach the server. Check your connection. Nothing was linked.',
      });
    }
  }

  async function handleRetryLink() {
    if (created === null) return;
    if (link === null) {
      setLinkFormError('Choose a line to link this row to.');
      return;
    }
    let answers: Record<string, 'manual' | 'document'> | null = null;
    if (linkOutcome?.state === 'conflict') {
      const missing = linkOutcome.details.filter((detail) => !(detail.path in resolutions));
      if (missing.length > 0) {
        setLinkFormError('Choose which value to keep for each field.');
        return;
      }
      answers = resolutions;
    }
    setLinkFormError(null);
    setSubmitting(true);
    await linkRow(created, link, answers);
    setSubmitting(false);
  }

  const companies = useSearchableOptions('companies');
  const products = useSearchableOptions('products');
  /*
   * Scoped to the chosen product. `null` fetches nothing and reports
   * `loading: false` — with no product there is no narrowing to do and every row
   * an unscoped list could offer would file this row under another product's
   * run, which nothing server-side refuses. Region, cycle and intake are `null`
   * deliberately: this record has none of the three, and their absence is also
   * what keeps the hook on its one-request path (see the header).
   */
  const projects = useLineProject(
    {
      productId: values.productId === '' ? null : Number(values.productId),
      regionId: null,
      cycleId: null,
      intakeId: null,
    },
    true,
  );
  const regions = useReferenceOptions('regions');

  const companyOptions = usePinnedChoice(companies.options, values.companyId);
  const productOptions = usePinnedChoice(products.options, values.productId);

  const projectOptions = useMemo<ComboboxOption[]>(
    () => projects.projects.map((project) => ({ value: String(project.id), label: project.name })),
    [projects.projects],
  );
  // Masters arrive in curated picker order (sortOrder ASC, name ASC) and are
  // rendered untouched — re-sorting alphabetically would discard that ordering.
  const regionOptions = useMemo(
    () => regions.records.map((record) => ({ value: String(record.id), label: record.name })),
    [regions.records],
  );
  const specialistOptions = useMemo<ComboboxOption[]>(
    () =>
      (specialists ?? []).map((specialist) => ({
        value: String(specialist.id),
        label: specialist.name,
        ...(specialist.role ? { hint: specialist.role } : {}),
      })),
    [specialists],
  );

  function setField<K extends keyof FormValues>(key: K, next: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: next }));
    setClientErrors((prev) => (key in prev ? without(prev, key) : prev));
    setServerErrors((prev) => (key in prev ? without(prev, key) : prev));
    // The date-order rule is reported against endDate, so moving either date
    // clears it — otherwise correcting the start leaves a stale message on the end.
    if (key === 'startDate') {
      setClientErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
      setServerErrors((prev) => ('endDate' in prev ? without(prev, 'endDate') : prev));
    }
  }

  /**
   * The product, and the project it invalidates, in ONE state update.
   *
   * ⚠️ **Not `setField('productId', …)` followed by `setField('projectId', '')`.**
   * The two would be separate updater functions over the same object, and the
   * form would be renderable — and submittable — between them holding a product
   * from one run and a project from another. `createStandaloneSchema` accepts
   * that pair, so the failure is a `201` and a tracker row filed under the wrong
   * project. Same shape as `{projectId, cityIds}` on a line and
   * `{cycleId, intakeId}` on a project: the guard runs on the post-merge state,
   * so the merge has to happen first.
   *
   * ⚠️ **Re-selecting the SAME product must not clear the project.** `Combobox`
   * fires `onChange` on every selection, including the one already held, and
   * wiping a deliberate choice because somebody reopened a dropdown and picked
   * what was already there is a silent loss with no refusal behind it.
   */
  function setProduct(next: string) {
    setValues((prev) =>
      prev.productId === next ? prev : { ...prev, productId: next, projectId: '' },
    );
    for (const setter of [setClientErrors, setServerErrors]) {
      setter((prev) =>
        'productId' in prev || 'projectId' in prev
          ? without(without(prev, 'productId'), 'projectId')
          : prev,
      );
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    const found = validate(values);
    setClientErrors(found);
    setServerErrors({});
    setFormError(null);
    if (Object.keys(found).length > 0) return;

    setSubmitting(true);
    try {
      const result = await api.createClientDeliverable(buildPayload(values));
      onCreated(result.data);
      if (link === null) {
        onClose();
        return;
      }
      /*
       * Step one is done and stays done: from here the modal reports, it never
       * creates again. See the header's "Two writes, no transaction".
       */
      setCreated(result.data);
      await linkRow(result.data, link, null);
      setSubmitting(false);
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details` at all, so it can never be a field error.
         * It can arrive even though the trigger was rendered, because a role can
         * change under a live access token — which lasts over five days. The
         * gate on the button is the optimisation; this is the guarantee.
         */
        if (caught.status === 403) {
          setFormError(`${caught.message} Nothing was created.`);
        } else {
          /*
           * Split against this form's OWN key list, never against the shape of
           * the path: a path can be non-empty and still bind to nothing, which
           * is the worse case because it looks bindable. `unroutable` carries
           * the `.strict()` empty-path refusals and anything naming a key with
           * no control here, and renders at form level.
           */
          const { fields, unroutable } = caught.routeFieldErrors(KNOWN_WRITE_KEYS);
          setServerErrors(fields);
          if (unroutable.length > 0) setFormError(unroutable.join(' '));
          else if (Object.keys(fields).length === 0) setFormError(caught.message);
        }
      } else {
        setFormError('Could not reach the server. Check your connection.');
      }
      setSubmitting(false);
    }
  }

  const fieldErrorCount = Object.keys(errors).length;
  /*
   * The three required values, and nothing else. Every other control is optional
   * server-side, so disabling on any of them would be a claim the API does not
   * make — and a wrongly disabled button is a false statement with nobody
   * watching, where a wrongly enabled one costs one refusal that says why.
   */
  const saveDisabled =
    values.companyId === '' || values.productId === '' || values.promised.trim() === '';

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      initialFocusRef={companyRef}
      title="New deliverable with no quote or invoice"
      footer={
        created !== null ? (
          <>
            {linkOutcome?.state !== 'linked' && (
              <Button
                variant="secondary"
                onClick={() => void handleRetryLink()}
                loading={submitting}
              >
                Link
              </Button>
            )}
            <Button variant="primary" onClick={onClose} disabled={submitting}>
              Done
            </Button>
          </>
        ) : (
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={submitting}
            disabled={saveDisabled}
          >
            Add to the tracker
          </Button>
        </>
        )
      }
    >
      {created !== null ? (
        <div className="space-y-3">
          <Notice tone="success" role="status">
            Added to the tracker.
          </Notice>
          {linkOutcome?.state === 'linked' && (
            <Notice tone="success" role="status">
              Linked to {linkOutcome.label}.
            </Notice>
          )}
          {linkOutcome?.state === 'failed' && (
            <Notice tone="danger" role="alert" title="Not linked">
              {linkOutcome.message}
            </Notice>
          )}
          {linkOutcome?.state === 'conflict' && (
            <div className="space-y-3">
              <Notice tone="warning" role="alert" title="Not linked">
                This row and the line disagree.
              </Notice>
              <ul className="space-y-2">
                {linkOutcome.details.map((detail) => (
                  <li key={detail.path} className="rounded-md border border-border px-3 py-2.5">
                    <fieldset className="min-w-0">
                      <legend className="text-sm font-medium text-fg">{detail.message}</legend>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <Radio
                          name={`${formId}-conflict-${detail.path}`}
                          checked={resolutions[detail.path] === 'manual'}
                          onChange={() =>
                            setResolutions((prev) => ({ ...prev, [detail.path]: 'manual' }))
                          }
                          disabled={submitting}
                          label={`This row: ${conflictValueText(detail.manual)}`}
                        />
                        <Radio
                          name={`${formId}-conflict-${detail.path}`}
                          checked={resolutions[detail.path] === 'document'}
                          onChange={() =>
                            setResolutions((prev) => ({ ...prev, [detail.path]: 'document' }))
                          }
                          disabled={submitting}
                          label={`The line: ${conflictValueText(detail.document)}`}
                        />
                      </div>
                    </fieldset>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {linkFormError !== null && (
            <Notice tone="danger" role="alert">
              {linkFormError}
            </Notice>
          )}
          {linkOutcome?.state !== 'linked' && (
            <section aria-labelledby={`${formId}-billed-on-retry`} className="space-y-2">
              <h3 id={`${formId}-billed-on-retry`} className="text-sm font-medium text-fg">
                Will be billed on
              </h3>
              <DocumentLinePicker
                candidates={recommendedCandidates}
                loading={linkCandidates.loading}
                error={linkCandidates.error}
                value={link}
                onChange={(next) => {
                  setLinkChoice({ key: linkKey, value: next });
                  setLinkFormError(null);
                  if (linkOutcome?.state === 'conflict') setLinkOutcome(null);
                }}
              />
            </section>
          )}
        </div>
      ) : (
      <form id={formId} onSubmit={handleSubmit} noValidate className="space-y-4">
        <FormErrorSummary formError={formError} fieldErrorCount={fieldErrorCount} />

        <div className="grid gap-3 sm:grid-cols-2">
          <div
            className="sm:col-span-2"
            ref={(node) => {
              companyRef.current = node?.querySelector('button') ?? null;
            }}
          >
            <Field label={LABELS.companyId} required error={errors.companyId}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  value={values.companyId}
                  onChange={(value) => setField('companyId', value)}
                  options={companyOptions}
                  placeholder="Select a client…"
                  searchPlaceholder="Search clients…"
                  onSearch={companies.setTerm}
                  loading={companies.loading && companyOptions.length === 0}
                  disabled={submitting}
                  invalid={Boolean(errors.companyId)}
                />
              )}
            </Field>
          </div>

          <Field label={LABELS.productId} required error={errors.productId}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.productId}
                // Not `setField`: choosing a product invalidates the project,
                // and the two must move together. See `setProduct`.
                onChange={setProduct}
                options={productOptions}
                placeholder="Select a product…"
                searchPlaceholder="Search products…"
                onSearch={products.setTerm}
                loading={products.loading && productOptions.length === 0}
                disabled={submitting}
                invalid={Boolean(errors.productId)}
              />
            )}
          </Field>

          <Field label={LABELS.promised} required error={errors.promised}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="numeric"
                autoComplete="off"
                value={values.promised}
                disabled={submitting}
                invalid={Boolean(errors.promised)}
                onChange={(event) => setField('promised', event.target.value)}
              />
            )}
          </Field>

          <Field
            label={LABELS.projectId}
            error={errors.projectId}
            /*
             * ⚠️ **THE SERVER'S ERROR, AND NOTHING ELSE — owner's ruling,
             * 2026-09-05.** This used to carry four branches; the other three
             * ("choose a product first", "this product is not sold through
             * projects", "only the first 100 … are listed") were this app's own
             * explanations of a why-disabled, a data state and a truncation, so
             * they went with the rest of the helper text.
             *
             * ⚠️ **The STATES are untouched and all three still gate `disabled`
             * below** — in particular `productHasNoProjects` is `boolean | null`
             * where `null` means NOBODY ASKED, and `=== true` is still written
             * out so the third state cannot be inherited from truthiness. What
             * is gone is only the sentence that named them.
             */
            hint={projects.error !== null ? projects.error : undefined}
          >
            {({ id, 'aria-describedby': describedBy }) => (
              // No `onSearch`: `useLineProject` fetches one product-scoped page
              // and the Combobox filters it locally. Wiring a term through would
              // send a param this call does not forward, and the picker would
              // look as though it had searched.
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.projectId}
                onChange={(value) => setField('projectId', value)}
                options={projectOptions}
                placeholder="No project"
                emptyOptionLabel="No project"
                searchPlaceholder="Filter projects…"
                loading={projects.loading}
                /*
                 * Disabled only where the reason is provable from data in hand
                 * and is stated in the hint above. `projectId` is optional on
                 * this record, so none of the three makes the form
                 * unsubmittable — the row saves with no project, which is what a
                 * product sold outside projects has.
                 */
                disabled={
                  submitting ||
                  values.productId === '' ||
                  projects.error !== null ||
                  projects.productHasNoProjects === true
                }
                invalid={Boolean(errors.projectId)}
              />
            )}
          </Field>

          <Field label={LABELS.billingLabel} error={errors.billingLabel}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                maxLength={BILLING_LABEL_MAX}
                autoComplete="off"
                value={values.billingLabel}
                disabled={submitting}
                invalid={Boolean(errors.billingLabel)}
                onChange={(event) => setField('billingLabel', event.target.value)}
              />
            )}
          </Field>

          <div className="sm:col-span-2">
            <Field label={LABELS.regionIds} error={errors.regionIds}>
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={values.regionIds}
                  onChange={(next) => setField('regionIds', next)}
                  options={regionOptions}
                  placeholder="No regions"
                  searchPlaceholder="Filter regions…"
                  loading={regions.loading}
                  maxSelected={REGIONS_MAX}
                  disabled={submitting}
                  // Form-staged: the set is reviewed before Save and undone by
                  // unticking, so a select-all is offered here. The live
                  // per-toggle region cells (`InlineRegionsField`) deliberately
                  // do NOT have it — there every tick is its own write.
                  selectAll
                  invalid={Boolean(errors.regionIds)}
                />
              )}
            </Field>
          </div>

          <Field label={LABELS.startDate} error={errors.startDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              // A native date input's value IS `YYYY-MM-DD`, built from local
              // parts — the wire format exactly, so nothing here builds a `Date`
              // and renders the day before west of Greenwich.
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={values.startDate}
                disabled={submitting}
                invalid={Boolean(errors.startDate)}
                onChange={(event) => setField('startDate', event.target.value)}
              />
            )}
          </Field>

          <Field label={LABELS.endDate} error={errors.endDate}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                value={values.endDate}
                min={values.startDate || undefined}
                disabled={submitting}
                invalid={Boolean(errors.endDate)}
                onChange={(event) => setField('endDate', event.target.value)}
              />
            )}
          </Field>

          <Field label={LABELS.engagementRole} error={errors.engagementRole}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.engagementRole}
                onChange={(value) => setField('engagementRole', value)}
                options={[...ENGAGEMENT_ROLE_OPTIONS]}
                placeholder="Not recorded"
                emptyOptionLabel="Not recorded"
                searchPlaceholder="Filter…"
                disabled={submitting}
                invalid={Boolean(errors.engagementRole)}
              />
            )}
          </Field>

          <Field label={LABELS.deliveryLeadId} error={errors.deliveryLeadId}>
            {({ id, 'aria-describedby': describedBy }) => (
              // Narrowed to the `delivery_lead` group by the caller; the server
              // deliberately does NOT enforce that group, so somebody missing
              // from this list is fixed by adding them to it.
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.deliveryLeadId}
                onChange={(value) => setField('deliveryLeadId', value)}
                options={staffOptions}
                placeholder="Use the client success manager"
                emptyOptionLabel="Use the client success manager"
                searchPlaceholder="Search staff…"
                disabled={submitting}
                invalid={Boolean(errors.deliveryLeadId)}
              />
            )}
          </Field>

          <Field label={LABELS.specialistId} error={errors.specialistId}>
            {({ id, 'aria-describedby': describedBy }) => (
              // `specialists === null` is unread or failed, never "nobody": the
              // control is disabled and the placeholder says so, rather than
              // offering an empty list whose only outcome is "No specialist".
              <Combobox
                id={id}
                aria-describedby={describedBy}
                value={values.specialistId}
                onChange={(value) => setField('specialistId', value)}
                options={specialistOptions}
                placeholder={
                  specialists === null ? 'Specialist list not available' : 'No specialist'
                }
                emptyOptionLabel="No specialist"
                searchPlaceholder="Search specialists…"
                disabled={submitting || specialists === null}
                invalid={Boolean(errors.specialistId)}
              />
            )}
          </Field>

          <Field label={LABELS.completed} error={errors.completed}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="numeric"
                autoComplete="off"
                placeholder="0"
                value={values.completed}
                disabled={submitting}
                invalid={Boolean(errors.completed)}
                onChange={(event) => setField('completed', event.target.value)}
              />
            )}
          </Field>

          <div className="sm:col-span-2">
            <Field label={LABELS.bookingSpec} error={errors.bookingSpec}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={3}
                  maxLength={BOOKING_SPEC_MAX}
                  value={values.bookingSpec}
                  disabled={submitting}
                  invalid={Boolean(errors.bookingSpec)}
                  onChange={(event) => setField('bookingSpec', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label={LABELS.billingNote} error={errors.billingNote}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  maxLength={BILLING_NOTE_MAX}
                  value={values.billingNote}
                  disabled={submitting}
                  invalid={Boolean(errors.billingNote)}
                  onChange={(event) => setField('billingNote', event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="sm:col-span-2">
            <Field label={LABELS.adsPlan} error={errors.adsPlan}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  maxLength={ADS_PLAN_MAX}
                  value={values.adsPlan}
                  disabled={submitting}
                  invalid={Boolean(errors.adsPlan)}
                  onChange={(event) => setField('adsPlan', event.target.value)}
                />
              )}
            </Field>
          </div>

          {linkKey !== '' && (
            <section aria-labelledby={`${formId}-billed-on`} className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between gap-2">
                <h3 id={`${formId}-billed-on`} className="text-sm font-medium text-fg">
                  Will be billed on
                </h3>
                {link !== null && (
                  <Button
                    variant="ghost"
                    onClick={() => setLinkChoice({ key: linkKey, value: null })}
                    disabled={submitting}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <DocumentLinePicker
                candidates={recommendedCandidates}
                loading={linkCandidates.loading}
                error={linkCandidates.error}
                value={link}
                onChange={(next) => setLinkChoice({ key: linkKey, value: next })}
              />
            </section>
          )}
        </div>
      </form>
      )}
    </Modal>
  );
}
